import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PricingSummaryDetails } from 'components/pricingSummaryDetails/PricingSummaryDetails';
import { OptionsGrid } from 'components/optionsGrid/OptionsGrid';
import { Spinner } from 'components/spinner/Spinner';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import { IPromoCode } from 'pages/newSubscription/models';
import { OptionsCache, useFetchOptions } from 'hooks/useFetchOptions';
import { SubscriptionsApi } from 'api/subscriptionsApi';
import { applySelection, findCellByPackage, resolveDefaultSelection } from 'subscriptionOptions/optionsGrid';
import { RenewalGroup } from 'subscriptionOptions/renewalGroups';
import {
  Basket,
  GridSelection,
  OptionsGridModel,
  OptionsType,
  PackageResult,
  PromoCodeBySubject,
} from 'subscriptionOptions/types';
import { codesForSubjects, promoCodeBySubject } from 'subscriptionOptions/renewalPromoCodes';
import { RenewalPromoCode } from 'subscriptionOptions/renewalPromoCodes';

export type SelectionChange = { subjectsId: number; packagesId: number | null };

export type RenewalPaymentRequest = {
  method: 'card' | 'invoice';
  subjectIds: number[];
  /** subjectsId -> packagesId, so the payment prices what the panel quoted. */
  selection: GridSelection;
  /** The codes the panel was priced under. */
  promoCodeBySubject: PromoCodeBySubject;
};

type ManageSubRenewalProps = {
  group: RenewalGroup;
  /** Page-level picks keyed by subject, so they survive a merge or split. */
  selection: GridSelection;
  onSelectionChange: (changes: SelectionChange[]) => void;
  optionsCache: OptionsCache;
  /** The account's codes, for every subject on the page. */
  promoCodes: RenewalPromoCode[];
  /** Set when the codes could not be read, which blocks pricing. */
  promoCodeError: string;
  /** A rejected code, reported against the promo box. */
  promoCodeRejection: string;
  /** Codes an apply displaced, so a missing row can be explained. */
  droppedPromoCodes: string[];
  /** Subjects the last package change wiped a code from; only this panel's own are shown. */
  wipedSubjectIds: number[];
  onApplyPromoCode: (promoCode: string) => void;
  onRemovePromoCode: (promoCode: string) => void;
  /** Fired after a package change is saved, with the response carrying its wipes. */
  onRenewalSaved: (responseData: any) => void;
  onOpenPayment: (request: RenewalPaymentRequest) => void;
  /** The packages as the summary last priced them; empty while it has no price. */
  onPackagesPriced: (packages: PackageResult[]) => void;
};

const SAVE_DEBOUNCE_MS = 400;

const SUBJECT_PENDING_MESSAGE = 'Payment will be available closer to your renewal date.';

const isComplete = (grid: OptionsGridModel, selection: GridSelection) =>
  grid.subjects.every((s) => selection[s.subjectsId] !== undefined);

function withoutSubjects<T>(bySubject: { [subjectsId: number]: T }, excluded: number[]) {
  const kept: { [subjectsId: number]: T } = {};
  Object.keys(bySubject)
    .map((k) => Number(k))
    .filter((id) => excluded.indexOf(id) === -1)
    .forEach((id) => {
      kept[id] = bySubject[id];
    });
  return kept;
}

/**
 * "Would you like to change your renewal?" panel for one auto-renew group. Its packages are priced
 * and paid as one basket, so the multi-subject discount only applies within a combined group.
 * Auto-renew ON pays by card or waits for an invoice on the renewal date; OFF pays by either now.
 * A subject outside its payment window (not bRenewable) can be changed but not yet paid for.
 */
export const ManageSubRenewal: React.FC<ManageSubRenewalProps> = ({
  group,
  selection,
  onSelectionChange,
  optionsCache,
  promoCodes,
  promoCodeError,
  promoCodeRejection,
  droppedPromoCodes,
  wipedSubjectIds,
  onApplyPromoCode,
  onRemovePromoCode,
  onRenewalSaved,
  onOpenPayment,
  onPackagesPriced,
}) => {
  const subjectIdsKey = group.subjects.map((s) => s.subjectsId).join(',');
  // Keyed on the ids, not the array: a list refetch rebuilds the group and would otherwise reprice.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const subjectIds = useMemo(() => (subjectIdsKey ? subjectIdsKey.split(',').map(Number) : []), [subjectIdsKey]);
  const { grid: fetchedGrid, accountContext, loading, error } = useFetchOptions(OptionsType.RENEW, true, {
    subjectsIDs: subjectIds,
    cache: optionsCache,
  });

  // Guards against an options response ignoring subjectsIDs, which would price another group's subjects.
  const warnedForGrid = useRef<OptionsGridModel | null>(null);
  const grid = useMemo<OptionsGridModel | null>(() => {
    if (!fetchedGrid) return null;
    const own = fetchedGrid.subjects.filter((s) => subjectIds.indexOf(s.subjectsId) !== -1);
    if (own.length === fetchedGrid.subjects.length) {
      return fetchedGrid;
    }
    if (warnedForGrid.current !== fetchedGrid) {
      warnedForGrid.current = fetchedGrid;
      console.warn(
        'Options returned subjects outside this renewal group',
        fetchedGrid.subjects.map((s) => s.subjectsId),
        subjectIds
      );
    }
    return { ...fetchedGrid, subjects: own, bMultipleSubjects: own.length > 1 };
  }, [fetchedGrid, subjectIds]);

  const [packageError, setPackageError] = useState<string>('');
  const [saveError, setSaveError] = useState<string>('');
  const [promoCode, setPromoCode] = useState<IPromoCode>({ value: '', error: '' });

  const saveTimer = useRef<number | null>(null);
  const saveSequence = useRef<number>(0);
  /**
   * True from the click until the save reports its wipes. Pricing waits, because pricing the old
   * codes first would quote a discount the school is about to lose.
   */
  const [selectionSaving, setSelectionSaving] = useState<boolean>(false);

  /** This panel's share of the account's codes, and the join pricing is built from. */
  const ownPromoCodes = useMemo(() => codesForSubjects(promoCodes, subjectIds), [promoCodes, subjectIds]);
  /** What this panel last showed, to tell whether a displaced code was ever its own. */
  const shownPromoCodes = useRef<string[]>([]);
  const ownPromoCodeBySubject = useMemo(() => promoCodeBySubject(ownPromoCodes), [ownPromoCodes]);

  // This panel's subjects only, dropping any pick the grid does not offer (kept from a previous grouping).
  const ownSelection = useMemo<GridSelection>(() => {
    const own: GridSelection = {};
    subjectIds.forEach((id) => {
      const packagesId = selection[id];
      if (packagesId === undefined) return;
      const subject = grid ? grid.subjects.find((s) => s.subjectsId === id) : undefined;
      if (!grid || (subject && findCellByPackage(subject, packagesId))) {
        own[id] = packagesId;
      }
    });
    return own;
  }, [selection, subjectIds, grid]);

  // Preselect the saved renewal package, else the current package, for subjects without an offered pick.
  useEffect(() => {
    if (!grid) return;

    const preferences: { [subjectsId: number]: (number | null)[] } = {};
    group.subjects.forEach((s) => {
      if (ownSelection[s.subjectsId] === undefined) {
        preferences[s.subjectsId] = [s.renewalPackagesId, s.currentPackagesId];
      }
    });

    const defaults = resolveDefaultSelection(grid, preferences);
    const changes = Object.keys(defaults).map((k) => ({ subjectsId: Number(k), packagesId: defaults[Number(k)] }));
    if (changes.length > 0) {
      onSelectionChange(changes);
    }
    // Grid only: re-running on selection would undo the school's own deselection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid]);

  useEffect(() => {
    return () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
      saveSequence.current += 1;
    };
  }, []);

  // Keyed on the ids for the same reason as subjectIds.
  const pendingIdsKey = group.subjects
    .filter((s) => !s.bRenewable)
    .map((s) => s.subjectsId)
    .join(',');
  const pendingSubjectIds = useMemo(
    () => (pendingIdsKey ? pendingIdsKey.split(',').map(Number) : []),
    [pendingIdsKey]
  );

  const basket = useMemo<Basket>(
    () => ({ selection: ownSelection, promoCodeBySubject: ownPromoCodeBySubject, pendingSubjectIds }),
    [ownSelection, ownPromoCodeBySubject, pendingSubjectIds]
  );

  // One package per subject, so an incomplete panel has no basket to price.
  const selectionComplete = !!grid && isComplete(grid, ownSelection);

  const subjectNotices = useMemo(() => {
    const notices: { [subjectsId: number]: string } = {};
    pendingSubjectIds.forEach((id) => {
      notices[id] = SUBJECT_PENDING_MESSAGE;
    });
    return notices;
  }, [pendingSubjectIds]);

  // Judged on what the grid offers: a payable subject it leaves out cannot be paid for here.
  const offeredSubjectIds = grid ? grid.subjects.map((s) => s.subjectsId) : subjectIds;
  const nothingPayable = offeredSubjectIds.every((id) => pendingSubjectIds.indexOf(id) !== -1);

  /** Displaced codes this panel was showing; the other panel's are not its news. */
  const ownDroppedPromoCodes = useMemo(
    () => droppedPromoCodes.filter((code) => shownPromoCodes.current.indexOf(code) !== -1),
    [droppedPromoCodes]
  );

  // Recorded after the comparison above, so a code is judged against what showed before the write.
  useEffect(() => {
    shownPromoCodes.current = ownPromoCodes.map((c) => c.promoCode);
  }, [ownPromoCodes]);

  /** A wipe can hit some subjects and not others, so the notice names the ones that lost it. */
  const wipedNotice = useMemo(() => {
    if (!grid || wipedSubjectIds.length === 0) return '';

    const names = grid.subjects
      .filter((s) => wipedSubjectIds.indexOf(s.subjectsId) !== -1)
      .map((s) => s.subjectsName);

    if (names.length === 0) return '';

    return (
      'Your promo code no longer applies to ' +
      names.join(' and ') +
      ' because the renewal package changed.'
    );
  }, [grid, wipedSubjectIds]);

  const persistSelection = (next: GridSelection) => {
    const payload = group.subjects
      .filter((s) => next[s.subjectsId] !== undefined)
      .map((s) => ({ schoolAccountPackagesId: s.schoolAccountPackagesId, packagesId: next[s.subjectsId] }));

    if (payload.length === 0) return;

    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    setSelectionSaving(true);

    saveTimer.current = window.setTimeout(async () => {
      saveTimer.current = null;
      setSaveError('');

      // Only the latest save may report back; an earlier one finishing later must not win.
      const sequence = ++saveSequence.current;
      const result = await SubscriptionsApi.setRenewalPackages({ setRenewalPackages: payload });
      if (sequence !== saveSequence.current) return;

      if (result.data && result.data.status === '1') {
        // The response reports which codes it wiped; the page subtracts them.
        onRenewalSaved(result.data);
      } else {
        setSaveError('Your renewal choice could not be saved. Please try again.');
      }

      // Releases pricing, now the codes this basket prices under are settled.
      setSelectionSaving(false);
    }, SAVE_DEBOUNCE_MS);
  };

  const handleSelect = (subjectsId: number, packagesId: number | null) => {
    if (!grid) return;
    if (packagesId !== null && ownSelection[subjectsId] === packagesId) return;

    const next = applySelection(ownSelection, subjectsId, packagesId);

    setPackageError('');
    onSelectionChange([{ subjectsId, packagesId }]);

    // Only a complete choice is saved: the endpoint expects one package per subject.
    if (packagesId !== null && isComplete(grid, next)) {
      persistSelection(next);
    }
  };

  /** Both panel kinds persist their codes: one held in state would be dropped by the next recall. */
  const handlePromoCodeApplied = (code: string) => {
    setSaveError('');
    onApplyPromoCode(code);
  };

  const openPayment = (method: 'card' | 'invoice') => {
    if (!grid) return;

    // Paying under codes we could not read would charge a price we cannot vouch for.
    if (promoCodeError || selectionSaving) return;

    if (!isComplete(grid, ownSelection)) {
      setPackageError(grid.subjects.length > 1 ? 'Please select a package for each subject' : 'Please select a package');
      return;
    }

    // Pending subjects stay out of the payment, matching the totals the summary showed.
    const payableSelection = withoutSubjects(ownSelection, pendingSubjectIds);
    const payableSubjectIds = Object.keys(payableSelection).map((k) => Number(k));
    if (payableSubjectIds.length === 0) return;

    onOpenPayment({
      method,
      subjectIds: payableSubjectIds,
      selection: payableSelection,
      promoCodeBySubject: withoutSubjects(ownPromoCodeBySubject, pendingSubjectIds),
    });
  };

  // Nothing offered, e.g. past the renewable period.
  if (!loading && !error && grid && grid.subjects.length === 0) {
    return null;
  }

  return (
    <div className="ManageSubRenewal">
      <div className="SRInner">
        <div className="SRCol">
          <h2>Would you like to change your renewal?</h2>
          <h5>Your renewal is currently set to match your existing subscription. If you'd like to make any changes, you can do so below.</h5>

          <ul>
            <li>Keep continuous service to receive a <strong>5% discount</strong>.</li>
            <li>Renew PE and Music together to receive a <strong>10% multi-subject discount</strong> (or contact us at <a href="mailto:admin@getset4education.co.uk"><strong>admin@getset4education.co.uk</strong></a> to align your subscriptions).</li>
            <li>If a promo code offers a larger saving, the highest discount will be applied.</li>
          </ul>

          {loading && (
            <div className="SRLoading">
              <Spinner color={'#f59e16'} />
            </div>
          )}

          {error && <FormGeneralError message={error} />}

          {grid && (
            <OptionsGrid
              grid={grid}
              selection={ownSelection}
              onSelect={handleSelect}
              presentation="table"
              hasError={!!packageError}
            />
          )}

          {packageError && <FormGeneralError message={packageError} />}
          {saveError && <FormGeneralError message={saveError} />}

          <ul className="SmList">
            <li>Infant (For access to all EYFS and KS1 areas)</li>
            <li>Junior (For access to all KS2 areas)</li>
            <li>Primary (For access to all EYFS, KS1 and KS2 areas)</li>
          </ul>
        </div>
        <div className="SRCol">
          <PricingSummaryDetails
            addPromoStep={true}
            promoCode={promoCode}
            setPromoCode={setPromoCode}
            existingMembershipOptInfo={null}
            accountContext={accountContext}
            basket={basket}
            basketReady={selectionComplete}
            basketPending={selectionSaving}
            appliedPromoCodes={ownPromoCodes}
            promoCodesError={promoCodeError}
            promoCodeRejection={promoCodeRejection}
            droppedPromoCodes={ownDroppedPromoCodes}
            wipedNotice={wipedNotice}
            onRemovePromoCode={onRemovePromoCode}
            btnText="Pay by card"
            initApply={false}
            renewalSummary={true}
            onContinue={() => openPayment('card')}
            secondBtnTxt={group.autoRenew ? undefined : 'Pay by invoice'}
            onSecondBtnContinue={group.autoRenew ? undefined : () => openPayment('invoice')}
            onPromoCodeApplied={handlePromoCodeApplied}
            subjectNotices={subjectNotices}
            paymentPending={nothingPayable}
            onPackagesPriced={onPackagesPriced}
          />
        </div>
      </div>
    </div>
  );
};
